import React, {createContext, ReactNode, useContext, useMemo} from 'react';
import {
  collection,
  collectionGroup,
  CollectionReference,
  getFirestore,
  limit,
  orderBy,
  query,
  Query
} from 'firebase/firestore';
import {User} from 'firebase/auth';
import {Deployment, Rollout, SetupTask} from '@finvari/deliver';
import {useSubscribedList} from "../utils/firebaseHooks";

const RECENT_ROLLOUTS_LIMIT = 100;
const RECENT_DEPLOYMENTS_LIMIT = 500;

interface RolloutWithDeployments extends Rollout {
  deployments: Deployment[];
}

interface LiveDeployment {
  prNumber: number | null;
  branch: string;
  prTitle?: string;
  rolloutId: string;
  deployments: Map<string, Deployment>; // module -> deployment
  isConsistent: boolean;
  inconsistentModules: string[];
}

interface PRDeploymentStatus {
  isCurrentlyDeployed: boolean;
  environments: string[]; // environments where this PR is currently deployed
  overallStatus: 'success' | 'in_progress' | 'failed' | 'mixed' | 'not_deployed';
  moduleCount: number;
}

interface DeploymentStatusContextType {
  rollouts: Rollout[];
  deployments: Deployment[];
  rolloutsWithDeployments: RolloutWithDeployments[];
  liveDeploymentsByEnvironment: Map<string, LiveDeployment>;
  getPRDeploymentStatus: (prNumber: number) => PRDeploymentStatus;
  getModuleDeploymentsForEnvironment: (environmentId: string) => Map<string, Deployment>;
  getDeploymentConsistencyInfo: (moduleDeployments: Map<string, Deployment>) => {
    isConsistent: boolean;
    primaryPR: number | null;
    primaryBranch: string;
    inconsistentModules: string[];
  };
  loading: boolean;
  error: string | null;
}

const DeploymentStatusContext = createContext<DeploymentStatusContextType | undefined>(undefined);

export const useDeploymentStatus = () => {
  const context = useContext(DeploymentStatusContext);
  if (context === undefined) {
    throw new Error('useDeploymentStatus must be used within a DeploymentStatusProvider');
  }
  return context;
};

interface DeploymentStatusProviderProps {
  children: ReactNode;
  user: User | null;
}

// The query shapes live here so the provider (which gates them on auth) and the
// standalone hook below stay in sync.
function recentRolloutsQuery(n: number): Query<Rollout> {
  const db = getFirestore();
  const rolloutsRef = collection(db, 'rollouts') as CollectionReference<Rollout>;
  return query(rolloutsRef, orderBy('createdAt', 'desc'), limit(n));
}

function recentDeploymentsQuery(n: number): Query<Deployment> {
  const db = getFirestore();
  const deploymentsRef = collectionGroup(db, 'deployments') as CollectionReference<Deployment>;
  return query(deploymentsRef, orderBy('createdAt', 'desc'), limit(n));
}

// Only safe to call from an authenticated route: it subscribes unconditionally,
// and firestore.rules deny every read to a signed-out caller.
export function useRecentDeployments(n = RECENT_DEPLOYMENTS_LIMIT) {
  const deploymentsQuery = useMemo(() => recentDeploymentsQuery(n), [n]);
  return useSubscribedList(deploymentsQuery, 'deployments');
}

export function useSetupTasksForRollout(rolloutId: string | undefined) {
  const setupTasksQuery = useMemo(() => {
    if (!rolloutId) return null;
    const db = getFirestore();
    const setupTasksRef = collection(db, 'rollouts', rolloutId, 'setupTasks') as CollectionReference<SetupTask>;
    return query(setupTasksRef, orderBy('createdAt', 'desc'));
  }, [rolloutId]);

  const { setupTasks, loading, error } = useSubscribedList<SetupTask, 'setupTasks'>(
    setupTasksQuery ?? undefined,
    'setupTasks'
  );

  return { setupTasks, loading, error };
}

export const DeploymentStatusProvider: React.FC<DeploymentStatusProviderProps> = ({ children, user }) => {
  // This provider mounts around the login route too, so it can be rendered while
  // signed out. Subscribing then gets a permission denial, which kills the
  // listener for good — the data stayed empty until a page reload even after the
  // user signed in. Wait for a user, matching the other context providers.
  const rolloutsQuery = useMemo(() => (user ? recentRolloutsQuery(RECENT_ROLLOUTS_LIMIT) : undefined), [user]);
  const deploymentsQuery = useMemo(() => (user ? recentDeploymentsQuery(RECENT_DEPLOYMENTS_LIMIT) : undefined), [user]);

  const { rollouts, ...rolloutsReq } = useSubscribedList<Rollout, 'rollouts'>(rolloutsQuery, 'rollouts');
  const { deployments, ...deploymentsReq } = useSubscribedList<Deployment, 'deployments'>(deploymentsQuery, 'deployments');

  const deploymentsByRolloutId: Map<string, Deployment[]> = useMemo(() => {
    const map = new Map<string, Deployment[]>();
    for (const deployment of deployments) {
      const { rolloutId } = deployment;
      if (!map.has(rolloutId)) {
        map.set(rolloutId, []);
      }
      map.get(rolloutId)?.push(deployment);
    }
    return map;
  }, [deployments]);

  const rolloutsWithDeployments: RolloutWithDeployments[] = useMemo(() => {
    return rollouts.map(rollout => ({
      ...rollout,
      deployments: deploymentsByRolloutId.get(rollout.rolloutId) ?? []
    }));
  }, [rollouts, deploymentsByRolloutId]);

  // Get the most recent deployment for each module in a specific environment
  // by looking at all rollouts and their nested deployments
  const getModuleDeploymentsForEnvironment = (environmentId: string): Map<string, Deployment> => {
    const moduleMap = new Map<string, Deployment>();

    for (const deployment of deployments) {
      if (deployment.environment === environmentId) {
        const current = moduleMap.get(deployment.module);
        if (!current || new Date(deployment.createdAt) > new Date(current.createdAt)) {
          moduleMap.set(deployment.module, deployment);
        }
      }
    }

    return moduleMap;
  };

  // Check if all modules are from the same PR/branch
  const getDeploymentConsistencyInfo = (moduleDeployments: Map<string, Deployment>) => {
    if (moduleDeployments.size === 0) {
      return { isConsistent: true, primaryPR: null, primaryBranch: '', inconsistentModules: [] };
    }

    const deployments = Array.from(moduleDeployments.values());
    const firstDeployment = deployments[0];
    const primaryPR = firstDeployment.prNumber;
    const primaryBranch = firstDeployment.branch;

    const inconsistentModules: string[] = [];

    moduleDeployments.forEach((deployment, module) => {
      if (deployment.prNumber !== primaryPR || deployment.branch !== primaryBranch) {
        inconsistentModules.push(module);
      }
    });

    return {
      isConsistent: inconsistentModules.length === 0,
      primaryPR,
      primaryBranch,
      inconsistentModules
    };
  };

  // Compute live deployments by environment
  const liveDeploymentsByEnvironment = useMemo((): Map<string, LiveDeployment> => {
    const environmentMap = new Map<string, LiveDeployment>();

    // Get all unique environments from rollouts
    const environments = new Set<string>();
    rollouts.forEach(rollout => {
      if (rollout.environment) {
        environments.add(rollout.environment);
      }
    });

    // For each environment, compute the live deployment
    environments.forEach(environmentId => {
      const moduleDeployments = getModuleDeploymentsForEnvironment(environmentId);
      const consistencyInfo = getDeploymentConsistencyInfo(moduleDeployments);

      if (moduleDeployments.size > 0) {
        const firstDeployment = Array.from(moduleDeployments.values())[0];

        // Find the most recent rollout for this environment that contributed deployments
        const relevantRollout = rollouts
          .filter(r => r.environment === environmentId)
          .find(r => deploymentsByRolloutId.get(r.rolloutId)?.some(d =>
            moduleDeployments.has(d.module) &&
            moduleDeployments.get(d.module)?.deploymentId === d.deploymentId
          ));

        environmentMap.set(environmentId, {
          prNumber: consistencyInfo.primaryPR,
          branch: consistencyInfo.primaryBranch,
          prTitle: firstDeployment.prTitle ?? undefined,
          rolloutId: relevantRollout?.rolloutId ?? '',
          deployments: moduleDeployments,
          isConsistent: consistencyInfo.isConsistent,
          inconsistentModules: consistencyInfo.inconsistentModules
        });
      }
    });

    return environmentMap;
    // Depends on deployments too: the body reads `deployments` (via
    // getModuleDeploymentsForEnvironment) and `deploymentsByRolloutId`. Omitting
    // them left this map stale whenever the deployments snapshot arrived after
    // the rollouts one. We list those data deps rather than the unmemoized
    // getModuleDeploymentsForEnvironment helper (which is recreated every render
    // and would defeat the memo).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rollouts, deployments, deploymentsByRolloutId]);

  // Function to get PR deployment status
  const getPRDeploymentStatus = (prNumber: number): PRDeploymentStatus => {
    const deployedEnvironments: string[] = [];
    let totalModuleCount = 0;
    const statuses = new Set<string>();

    // Check each environment to see if this PR is currently deployed there
    liveDeploymentsByEnvironment.forEach((liveDeployment, environmentId) => {
      if (liveDeployment.prNumber === prNumber) {
        deployedEnvironments.push(environmentId);
      }
    });

    // Get all deployments for this PR from rollouts
    for (const deployment of deployments) {
      if (deployment.prNumber === prNumber) {
        totalModuleCount++;
        statuses.add(deployment.status);
      }
    }

    // Determine overall status
    let overallStatus: 'success' | 'in_progress' | 'failed' | 'mixed' | 'not_deployed' = 'not_deployed';

    if (deployedEnvironments.length === 0) {
      overallStatus = 'not_deployed';
    } else if (statuses.has('in_progress') || statuses.has('pending')) {
      overallStatus = 'in_progress';
    } else if (statuses.has('failed')) {
      if (statuses.has('success')) {
        overallStatus = 'mixed';
      } else {
        overallStatus = 'failed';
      }
    } else if (statuses.has('success')) {
      overallStatus = 'success';
    }

    return {
      isCurrentlyDeployed: deployedEnvironments.length > 0,
      environments: deployedEnvironments,
      overallStatus,
      moduleCount: totalModuleCount
    };
  };

  const value: DeploymentStatusContextType = {
    rollouts,
    deployments,
    rolloutsWithDeployments,
    liveDeploymentsByEnvironment,
    getPRDeploymentStatus,
    getModuleDeploymentsForEnvironment,
    getDeploymentConsistencyInfo,
    loading: rolloutsReq.loading || deploymentsReq.loading,
    error: rolloutsReq.error || deploymentsReq.error || null,
  };

  return (
    <DeploymentStatusContext.Provider value={value}>
      {children}
    </DeploymentStatusContext.Provider>
  );
};
